import React, { useMemo } from 'react';
import {
  Box,
  Flex,
  Grid,
  Text,
  Spinner,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import moment from 'moment';
import { Scroll, UsersThree, ChartPie, Hash, TrendUp } from 'phosphor-react';
import { useAuditoriaInteligente } from '../../../../hooks/useAuditoriaInteligente';
import { num, pct, qtd } from '../format';
import { IAuditoriaEmpresa, ISocio } from '../types';
import { KpiCard } from '../components/KpiCard';
import { InfoBloco, FILTRO } from '../components/InfoPopover';
import { EmptyState } from '../components/EmptyState';
import { UI } from '../ui';

/** Só dígitos do documento → PF (≤11) ou PJ. */
const ehPJ = (doc?: string) => (doc ?? '').replace(/\D/g, '').length > 11;
/** Anos desde o ingresso (parse tolerante). */
const anosDesde = (d?: string): number | null => {
  const m = moment(d, ['YYYY-MM-DD', 'DD/MM/YYYY', moment.ISO_8601], true);
  return m.isValid() ? Math.max(0, moment().diff(m, 'years', true)) : null;
};

interface SocietarioScreenProps {
  empresa: IAuditoriaEmpresa | null;
}

const CORES = [UI.brand, UI.pos, UI.warn, '#8B5CF6', '#1AA6B7', UI.neg];

export function SocietarioScreen({ empresa }: SocietarioScreenProps) {
  const { getSocietario } = useAuditoriaInteligente();

  const { data, isFetching } = useQuery<ISocio[]>({
    queryKey: ['ai-societario', empresa?.id],
    queryFn: getSocietario,
    enabled: !!empresa,
  });

  const socios = useMemo(() => data ?? [], [data]);
  const ativos = useMemo(() => socios.filter(s => s.status === 'Ativo'), [socios]);

  const resumo = useMemo(() => {
    const participacao = ativos.reduce((s, x) => s + num(x.participacao), 0);
    const quotas = ativos.reduce((s, x) => s + num(x.quotas), 0);
    const ordenados = [...ativos].sort((a, b) => num(b.participacao) - num(a.participacao));
    const majoritario = ordenados[0];
    const top2 = ordenados.slice(0, 2).reduce((s, x) => s + num(x.participacao), 0);
    const pj = ativos.filter(s => ehPJ(s.cpf_cnpj)).length;
    const anos = ativos.map(s => anosDesde(s.data_ingresso)).filter((a): a is number => a !== null);
    const permanencia = anos.length ? anos.reduce((s, a) => s + a, 0) / anos.length : 0;
    return { participacao, quotas, majoritario, top2, pf: ativos.length - pj, pj, permanencia };
  }, [ativos]);

  const pie = useMemo(
    () =>
      ativos.map(s => ({
        name: (s.nome ?? '').split(' ').slice(0, 2).join(' '),
        value: num(s.participacao),
      })),
    [ativos],
  );

  if (!empresa) {
    return (
      <EmptyState
        title="Selecione uma empresa"
        message="Use a busca no topo (ID, CNPJ ou Razão Social) para ver o quadro societário."
        icon={Scroll}
      />
    );
  }

  return (
    <Box>
      <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap={4} mb={5}>
        <KpiCard
          label="Sócios ativos"
          value={ativos.length}
          icon={UsersThree}
          iconColor={UI.brand}
          iconBg={UI.infoSoft}
          isLoading={isFetching}
          info={
            <InfoBloco
              oque="Quantidade de sócios atualmente no quadro societário da empresa (sem data de saída registrada)."
              dados="extract_socios.data_saida IS NULL (status = 'Ativo'), filtrado por external_id"
              filtro={FILTRO.empresaSemPeriodo}
            />
          }
        />
        <KpiCard
          label="Participação total"
          value={pct(resumo.participacao)}
          sub={Math.round(resumo.participacao) === 100 ? 'Capital integralizado' : 'Verificar'}
          icon={ChartPie}
          iconColor={UI.pos}
          iconBg={UI.posSoft}
          isLoading={isFetching}
          info={
            <InfoBloco
              oque="Soma das participações (%) de todos os sócios ativos. Próximo de 100% indica capital integralizado."
              dados="SUM(extract_socios.participacao) dos sócios com data_saida IS NULL"
              filtro={FILTRO.empresaSemPeriodo}
            />
          }
        />
        <KpiCard
          label="Quotas totais"
          value={qtd(resumo.quotas)}
          icon={Hash}
          iconColor={UI.warn}
          iconBg={UI.warnSoft}
          isLoading={isFetching}
          info={
            <InfoBloco
              oque="Soma do número de quotas detidas por todos os sócios ativos da empresa."
              dados="SUM(extract_socios.quotas) dos sócios com data_saida IS NULL"
              filtro={FILTRO.empresaSemPeriodo}
            />
          }
        />
        <KpiCard
          label="Sócio majoritário"
          value={resumo.majoritario ? pct(resumo.majoritario.participacao) : '—'}
          sub={resumo.majoritario?.nome?.split(' ').slice(0, 2).join(' ')}
          icon={TrendUp}
          iconColor="#8B5CF6"
          iconBg="#F0EAFB"
          isLoading={isFetching}
          info={
            <InfoBloco
              oque="Sócio com a maior participação (%) no quadro societário ativo, com o respectivo percentual."
              dados="extract_socios.nome e participacao do sócio ativo com maior participacao (ORDER BY participacao DESC)"
              filtro={FILTRO.empresaSemPeriodo}
            />
          }
        />
      </Grid>

      <Grid templateColumns={{ base: '1fr', lg: '3fr 2fr' }} gap={5} mb={5}>
        {pie.length > 0 && (
          <Box
            bg={UI.glass}
            border="1px solid"
            borderColor={UI.glassBorder}
            borderRadius="16px"
            backdropFilter={UI.glassBlur}
            boxShadow={UI.glassShadow}
            p={5}
          >
            <Text fontSize="13px" fontWeight={700} mb={4} color={UI.ink}>
              Participação societária
            </Text>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={pie}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={2}
                >
                  {pie.map((entry, i) => (
                    <Cell key={entry.name} fill={CORES[i % CORES.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => `${v}%`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        )}

        <Box
          bg={UI.glass}
          border="1px solid"
          borderColor={UI.glassBorder}
          borderRadius="16px"
          backdropFilter={UI.glassBlur}
          boxShadow={UI.glassShadow}
          p={5}
        >
          <Text fontSize="13px" fontWeight={700} mb={4} color={UI.ink}>
            Composição
          </Text>
          {[
            { k: 'Concentração Top 2', v: pct(resumo.top2), alerta: resumo.top2 >= 90 },
            { k: 'Pessoa Física', v: `${resumo.pf} sócio(s)` },
            { k: 'Pessoa Jurídica', v: `${resumo.pj} sócio(s)` },
            {
              k: 'Permanência média',
              v: resumo.permanencia ? `${resumo.permanencia.toFixed(1)} anos` : '—',
            },
          ].map(r => (
            <Flex
              key={r.k}
              justify="space-between"
              align="center"
              py={2.5}
              borderBottom="1px solid #EEF2F6"
            >
              <Text fontSize="12.5px" color={UI.muted}>
                {r.k}
              </Text>
              <Text fontSize="13px" fontWeight={800} color={r.alerta ? UI.warn : UI.ink}>
                {r.v}
              </Text>
            </Flex>
          ))}
        </Box>
      </Grid>

      <Box
        borderRadius="16px"
        overflow="hidden"
        bg="rgba(255,255,255,0.7)"
        backdropFilter="blur(14px) saturate(160%)"
        border="1px solid rgba(255,255,255,0.85)"
        boxShadow="0 12px 34px -18px rgba(15,23,42,0.35)"
      >
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead bg={UI.rowHover}>
              <Tr>
                <Th>Nome</Th>
                <Th>CPF/CNPJ</Th>
                <Th>Tipo</Th>
                <Th>Cargo</Th>
                <Th isNumeric>Participação</Th>
                <Th isNumeric>Quotas</Th>
                <Th>Ingresso</Th>
                <Th>Situação</Th>
              </Tr>
            </Thead>
            <Tbody>
              {isFetching && (
                <Tr>
                  <Td colSpan={8} textAlign="center" py={8}>
                    <Spinner />
                  </Td>
                </Tr>
              )}
              {!isFetching &&
                socios.map(s => (
                  <Tr key={`${s.cpf_cnpj}-${s.nome}`} _hover={{ bg: UI.rowHover }}>
                    <Td fontWeight={700} maxW="240px" whiteSpace="normal">
                      {s.nome}
                    </Td>
                    <Td>{s.cpf_cnpj}</Td>
                    <Td>
                      <Badge
                        style={
                          ehPJ(s.cpf_cnpj)
                            ? { backgroundColor: '#F0EAFB', color: '#8B5CF6' }
                            : { backgroundColor: UI.infoSoft, color: UI.brand }
                        }
                        borderRadius="full"
                        px={2}
                        fontSize="10.5px"
                        fontWeight={700}
                        textTransform="none"
                      >
                        {ehPJ(s.cpf_cnpj) ? 'PJ' : 'PF'}
                      </Badge>
                    </Td>
                    <Td>{s.cargo}</Td>
                    <Td isNumeric fontWeight={700}>
                      {pct(s.participacao)}
                    </Td>
                    <Td isNumeric>{qtd(s.quotas)}</Td>
                    <Td>{s.data_ingresso}</Td>
                    <Td>
                      {s.status === 'Ativo' ? (
                        <Badge
                          style={{ backgroundColor: UI.posSoft, color: UI.pos }}
                          borderRadius="full"
                          px={2.5}
                          py={0.5}
                          fontSize="11.5px"
                          fontWeight={700}
                          textTransform="none"
                        >
                          Ativo
                        </Badge>
                      ) : (
                        <Badge
                          style={{ backgroundColor: '#EFF2F6', color: UI.muted }}
                          borderRadius="full"
                          px={2.5}
                          py={0.5}
                          fontSize="11.5px"
                          fontWeight={700}
                          textTransform="none"
                        >
                          Inativo{s.data_saida ? ` · ${s.data_saida}` : ''}
                        </Badge>
                      )}
                    </Td>
                  </Tr>
                ))}
              {!isFetching && socios.length === 0 && (
                <Tr>
                  <Td colSpan={8} textAlign="center" py={8} color={UI.muted}>
                    Nenhum sócio cadastrado.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
